import { motion } from "framer-motion";
import { MapPinned, MonitorSmartphone, WifiOff, FileUp } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { PhoneMockup } from "@/components/PhoneMockup";
import { SCREENS } from "@/data/content";

const ICONS = [MapPinned, MonitorSmartphone, WifiOff, FileUp];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] },
};

export const Revier = () => {
  const { t } = useLanguage();
  const s = t.revier;

  return (
    <section id="revier" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-6 md:px-8 lg:grid-cols-12 lg:gap-10">
        <motion.div {...reveal} className="lg:col-span-6">
          <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.body} />

          <div className="mt-7 flex flex-wrap gap-2">
            {s.chips.map((c) => (
              <span key={c} className="rounded-full border border-[var(--line-strong)] px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-2)]">
                {c}
              </span>
            ))}
          </div>

          <dl className="mt-10 grid gap-x-8 gap-y-7 sm:grid-cols-2">
            {s.points.map((p, i) => {
              const Icon = ICONS[i] || MapPinned;
              return (
                <div key={p.title} className="flex gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                    <Icon className="h-5 w-5 text-[var(--sage)]" />
                  </span>
                  <div>
                    <dt className="font-semibold text-[var(--ink)]">{p.title}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-[var(--ink-2)]">{p.desc}</dd>
                  </div>
                </div>
              );
            })}
          </dl>
        </motion.div>

        <div className="relative lg:col-span-6">
          <div className="relative mx-auto h-[620px] w-full max-w-[520px] sm:h-[700px]">
            <div className="absolute inset-x-12 top-10 bottom-10 rounded-[3rem] bg-[var(--forest)]/40 blur-3xl" aria-hidden="true" />
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="absolute right-2 top-0 w-[270px] sm:right-6 sm:w-[300px]"
            >
              <PhoneMockup src={SCREENS.revierKarte} alt={s.screenMain} testId="revier-phone-main" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="absolute bottom-0 left-0 w-[200px] sm:left-2 sm:w-[230px]"
            >
              <PhoneMockup src={SCREENS.revierAnsitz} alt={s.screenDetail} testId="revier-phone-detail" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="card absolute left-1/2 top-[46%] z-10 -translate-x-1/2 px-4 py-3 shadow-2xl backdrop-blur"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--ink-3)]">{s.screenDetail}</p>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#2f8f4e]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#e0a52c]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#c53b3b]" />
                <span className="ml-1 text-xs text-[var(--ink-2)]">Windampel</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
